import dayjs from "dayjs";
import type { ClubMembership, Invoice } from "./types";

export type MembershipInvoiceRow = Invoice & {
  club_name: string;
  group_name: string;
};

export const getUnpaidInvoiceCount = (club: ClubMembership | null | undefined): number => {
  if (!club) return 0;
  const groups = Array.isArray(club.training_groups) ? club.training_groups : [];
  return groups.reduce((count, group) => {
    const invoices = Array.isArray(group?.invoices) ? group.invoices : [];
    return (
      count +
      invoices.filter(
        (invoice) => invoice && !invoice.paid && !invoice.voided_at,
      ).length
    );
  }, 0);
};

/** Flatten and sort invoices from /my-memberships for display lists. */
export const collectMembershipInvoices = (
  memberships: ClubMembership[] | null | undefined,
): MembershipInvoiceRow[] => {
  if (!Array.isArray(memberships)) {
    return [];
  }

  const rows: MembershipInvoiceRow[] = [];

  for (const club of memberships) {
    if (!club || typeof club.name !== "string") {
      continue;
    }
    const groups = Array.isArray(club.training_groups) ? club.training_groups : [];
    for (const group of groups) {
      if (!group || typeof group.group_name !== "string") {
        continue;
      }
      const invoices = Array.isArray(group.invoices) ? group.invoices : [];
      for (const invoice of invoices) {
        if (!invoice || typeof invoice.id !== "number") {
          continue;
        }
        rows.push({
          id: invoice.id,
          amount: typeof invoice.amount === "number" ? invoice.amount : 0,
          period_start: invoice.period_start ?? "",
          period_end: invoice.period_end ?? "",
          paid: Boolean(invoice.paid),
          created_at: invoice.created_at ?? "",
          due_date: invoice.due_date ?? "",
          voided_at: invoice.voided_at ?? null,
          club_name: club.name,
          group_name: group.group_name,
        });
      }
    }
  }

  return rows.sort((a, b) => {
    const aVoided = Boolean(a.voided_at);
    const bVoided = Boolean(b.voided_at);
    if (aVoided !== bVoided) {
      return aVoided ? 1 : -1;
    }
    if (a.paid !== b.paid) {
      return a.paid ? 1 : -1;
    }
    if (!a.paid && !aVoided) {
      return dayjs(a.due_date).valueOf() - dayjs(b.due_date).valueOf();
    }
    return dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf();
  });
};

export const getNextTrainingDate = (
  club: ClubMembership,
): { group_name: string; training_date: string; days_until: number } | null => {
  if (!club.upcoming_training_dates?.length) {
    return null;
  }

  return [...club.upcoming_training_dates].sort((a, b) =>
    dayjs(a.training_date).diff(dayjs(b.training_date)),
  )[0];
};
